/**
 * 13 #9 — "Tidy the register": a one-line callout over the All Drawings table that says what is messy
 * ("370 drawings have no Drawing ID (typed 5 ways) · 114 have no Responsible · Drawing Type is spelled 17 ways")
 * and a wizard that fixes it through the existing bulk-edit endpoint (each drawing keeps the change in its
 * history; one undo step per 500 drawings):
 *   1. every placeholder Drawing ID ('-', 'NOID', 'NONE', 'NO_ID') written the one way, 'NO ID';
 *   2. Drawing Type spellings merged ('FLOOR PLAN' → 'Floor Plan', 'SCHIMATIC' → 'Schematic');
 *   3. a Responsible for drawings without one, per project (the project's usual person is suggested);
 *   4. Drawing IDs used by more than one drawing (NOBU HOTEL and NOBU Residences) listed to review.
 */
import { useMemo, useState } from 'react';
import { ArrowRight, ExternalLink, Loader2, WandSparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatNumber } from '@/lib/format';
import { readStored, storageKey, writeStored } from '@/lib/storage';
import { cn } from '@/lib/utils';
import { NO_ID_VALUE, buildTidyChanges, displayPerson, drawingTitle } from './model';
import { SdCheckbox } from './SdUi';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const DISMISS_KEY = storageKey('sd', 'all', 'tidyCallout');

/** What the callout reports; '' when there is nothing to tidy. Changes when the register does. */
export const tidySignature = (tidy) => (tidy ? `${tidy.idChanges.length}:${tidy.types.filter((item) => !item.caseOnly).length}:${tidy.missingRespCount}` : '');

function Ways({ spellings }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-1 align-middle">
      {spellings.map((item) => (
        <span key={item.value} className="inline-flex h-[18px] items-center rounded-[5px] border border-[color:rgb(217_119_6/0.35)] bg-surface px-1 font-mono text-[10.5px] font-semibold text-status-amber-ink">
          {item.value}
        </span>
      ))}
    </span>
  );
}

export function TidyCallout({ tidy, onReview }) {
  const signature = tidySignature(tidy);
  const [dismissed, setDismissed] = useState(() => readStored(DISMISS_KEY, ''));
  const typeMerges = tidy ? tidy.types.filter((item) => !item.caseOnly) : [];
  if (!tidy || (!tidy.idChanges.length && !typeMerges.length && !tidy.missingRespCount) || dismissed === signature) return null;
  const parts = [];
  if (tidy.noIdCount) {
    parts.push(
      <span key="ids">
        <b className="font-semibold">{fmt(tidy.noIdCount)}</b> drawings have no Drawing ID
        {tidy.idSpellings.length > 1 ? <> <span className="text-ink-3">(typed {tidy.idSpellings.length} ways</span> <Ways spellings={tidy.idSpellings} /><span className="text-ink-3">)</span></> : null}
      </span>
    );
  }
  if (tidy.missingRespCount) parts.push(<span key="resp"><b className="font-semibold">{fmt(tidy.missingRespCount)}</b> have no Responsible</span>);
  if (typeMerges.length) parts.push(<span key="types">Type spelled <b className="font-semibold">{fmt(tidy.typeCount)}</b> ways</span>);
  return (
    <div
      role="status"
      data-testid="tidy-callout"
      className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border border-[color:rgb(217_119_6/0.28)] bg-status-amber-bg px-3.5 py-2 text-[13px] text-ink"
    >
      <WandSparkles className="h-4 w-4 shrink-0 text-status-amber-ink" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-[12.5px] leading-6 xl:text-[13px]">
        <b className="font-bold">Tidy the register:</b>{' '}
        {parts.map((part, index) => <span key={part.key}>{index ? <span className="mx-1.5 text-ink-4" aria-hidden="true">·</span> : null}{part}</span>)}
      </p>
      {/* Below sm the buttons go under the message, so it uses the full width of a phone. */}
      <div className="ml-auto flex shrink-0 items-center gap-1 max-sm:w-full max-sm:justify-between max-sm:pl-7">
        <Button type="button" size="sm" variant="outline" onClick={onReview} data-testid="tidy-review-btn">Review fixes</Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Hide until the register changes"
          title="Hide until the register changes"
          onClick={() => { writeStored(DISMISS_KEY, signature); setDismissed(signature); }}
          data-testid="tidy-dismiss"
        >
          <X aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

function Section({ checked, onCheckedChange, title, meta, children, testId, disabled = false }) {
  return (
    <section className={cn('rounded-xl border px-3.5 py-3', checked ? 'border-[color:var(--brand-soft-border)] bg-[color:var(--navy-50)]' : 'border-line')} data-testid={testId}>
      <label className={cn('flex items-start gap-2.5', disabled ? 'cursor-default' : 'cursor-pointer')}>
        {onCheckedChange ? <SdCheckbox className="mt-0.5" checked={checked} disabled={disabled} onCheckedChange={(value) => onCheckedChange(Boolean(value))} aria-label={title} data-testid={`${testId}-check`} /> : null}
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-bold text-ink">{title}</span>
          {meta ? <span className="mt-0.5 block text-[12.5px] text-ink-3">{meta}</span> : null}
        </span>
      </label>
      {children ? <div className={cn('mt-2.5', onCheckedChange && 'pl-[26px]')}>{children}</div> : null}
    </section>
  );
}

export function TidyDialog({ open, onOpenChange, drawings, tidy, people = [], onApply, onOpenDrawing }) {
  const [idsOn, setIdsOn] = useState(true);
  const [types, setTypes] = useState({});
  const [owners, setOwners] = useState({});
  const [showAllDuplicates, setShowAllDuplicates] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [lastOpen, setLastOpen] = useState(false);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open && tidy) {
      setIdsOn(tidy.idChanges.length > 0);
      setTypes(Object.fromEntries(tidy.types.map((item) => [item.key, !item.caseOnly])));
      setOwners(Object.fromEntries(tidy.people.map((item) => [item.key, item.suggested || ''])));
      setShowAllDuplicates(false);
      setError('');
    }
  }

  const fixes = useMemo(() => {
    if (!tidy) return [];
    const list = [];
    if (idsOn && tidy.idChanges.length) list.push({ ids: tidy.idChanges, field: 'drawingId', value: NO_ID_VALUE, note: `Drawing ID written as “${NO_ID_VALUE}”` });
    tidy.types.forEach((item) => {
      if (types[item.key]) list.push({ ids: item.ids, field: 'dtype', value: item.to, note: `Drawing Type written as “${item.to}”` });
    });
    tidy.people.forEach((item) => {
      if (owners[item.key]) list.push({ ids: item.ids, field: 'resp', value: owners[item.key], note: `Responsible set to ${owners[item.key]}` });
    });
    return list;
  }, [idsOn, owners, tidy, types]);
  const chunks = useMemo(() => buildTidyChanges(drawings, fixes), [drawings, fixes]);
  const changeCount = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const byId = useMemo(() => new Map(drawings.map((drawing) => [drawing.id, drawing])), [drawings]);
  const personOptions = useMemo(() => [...new Set(people.filter(Boolean))].sort((a, b) => a.localeCompare(b)), [people]);

  const apply = async () => {
    if (!changeCount) return;
    setBusy(true);
    setError('');
    try {
      await onApply(chunks);
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || 'Could not apply the fixes');
    } finally {
      setBusy(false);
    }
  };

  if (!tidy) return null;
  const duplicates = showAllDuplicates ? tidy.duplicates : tidy.duplicates.slice(0, 6);
  const crossProject = tidy.duplicates.filter((item) => item.crossProject).length;

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-hidden rounded-2xl border-line bg-surface p-0 text-ink" data-testid="tidy-dialog">
        <div className="flex max-h-[90vh] min-w-0 flex-col">
          <DialogHeader className="border-b border-line-2 px-5 pb-3 pt-5 text-left">
            <DialogTitle className="flex items-center gap-2 font-heading text-[17px] font-bold text-ink">
              <WandSparkles className="h-[18px] w-[18px] text-status-amber-ink" aria-hidden="true" /> Tidy the register
            </DialogTitle>
            <DialogDescription className="text-[13px] text-ink-3">
              Tick the fixes you agree with. Each drawing keeps the change in its history, and the message afterwards has Undo.
            </DialogDescription>
          </DialogHeader>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
            {tidy.idChanges.length ? (
              <Section
                testId="tidy-ids"
                checked={idsOn}
                onCheckedChange={setIdsOn}
                title={`Write every missing Drawing ID the same way: “${NO_ID_VALUE}”`}
                meta={`${fmt(tidy.noIdCount)} drawings have no Drawing ID yet. ${fmt(tidy.idChanges.length)} of them are typed another way; the list shows all of them as a “No ID” tag.`}
              >
                <div className="flex flex-wrap items-center gap-1.5 text-[12.5px] text-ink-2">
                  {tidy.idSpellings.map((item) => (
                    <span key={item.value} className={cn('inline-flex h-6 items-center gap-1.5 rounded-md border px-2', item.value === NO_ID_VALUE ? 'border-line bg-surface text-ink-3' : 'border-[color:rgb(217_119_6/0.35)] bg-status-amber-bg')}>
                      <span className="font-mono text-[11.5px] font-semibold">{item.value}</span>
                      <span className="tabular-nums text-ink-3">{fmt(item.count)}</span>
                    </span>
                  ))}
                  <ArrowRight className="h-3.5 w-3.5 text-ink-4" aria-hidden="true" />
                  <span className="inline-flex h-6 items-center rounded-md bg-brand-soft px-2 font-mono text-[11.5px] font-bold text-[color:var(--brand-ink)]">{NO_ID_VALUE}</span>
                </div>
              </Section>
            ) : null}

            {tidy.types.length ? (
              <Section
                testId="tidy-types"
                title={`Merge Drawing Type spellings (${fmt(tidy.typeCount)} today)`}
                meta="Spellings that are the same type become the one most of the register uses."
              >
                <ul className="divide-y divide-line-2 overflow-hidden rounded-lg border border-line bg-surface">
                  {tidy.types.map((item) => (
                    <li key={item.key}>
                      <label className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-[12.5px] hover:bg-surface-hover">
                        <SdCheckbox checked={Boolean(types[item.key])} onCheckedChange={(value) => setTypes((current) => ({ ...current, [item.key]: Boolean(value) }))} aria-label={`Write as ${item.to}`} data-testid={`tidy-type-${item.key}`} />
                        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                          {item.spellings.filter((spelling) => spelling.value !== item.to).map((spelling) => (
                            <span key={spelling.value} className="inline-flex items-center gap-1 rounded bg-surface-2 px-1.5 py-px">
                              <span className="font-semibold text-ink-2">{spelling.value}</span>
                              <span className="tabular-nums text-ink-3">{fmt(spelling.count)}</span>
                            </span>
                          ))}
                          <ArrowRight className="h-3.5 w-3.5 text-ink-4" aria-hidden="true" />
                          <span className="font-bold text-ink">{item.to}</span>
                          {item.caseOnly ? <span className="text-[11.5px] text-ink-3">· usual case</span> : null}
                        </span>
                        <span className="shrink-0 text-[11.5px] tabular-nums text-ink-3">{fmt(item.ids.length)} change</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}

            {tidy.people.length ? (
              <Section
                testId="tidy-people"
                title={`Fill in the Responsible (${fmt(tidy.missingRespCount)} drawings have none)`}
                meta="Per project; the person most of the project’s drawings have is suggested. Leave “Nobody” to skip a project."
              >
                <ul className="divide-y divide-line-2 overflow-hidden rounded-lg border border-line bg-surface">
                  {tidy.people.map((item) => (
                    <li key={item.key} className="flex flex-wrap items-center gap-2 px-3 py-2 text-[12.5px]">
                      <span className="min-w-0 flex-1 truncate">
                        <b className="font-semibold text-ink">{item.project}</b>
                        {item.projectNo ? <span className="ml-1 font-mono text-[11px] text-ink-3">#{item.projectNo}</span> : null}
                        <span className="ml-1.5 text-ink-3">· {fmt(item.ids.length)} without a Responsible</span>
                      </span>
                      <select
                        value={owners[item.key] || ''}
                        onChange={(event) => setOwners((current) => ({ ...current, [item.key]: event.target.value }))}
                        className="h-8 max-w-[180px] rounded-lg border border-line bg-surface px-2 text-[12.5px] font-semibold text-ink outline-none focus:ring-2 focus:ring-ring"
                        aria-label={`Responsible for ${item.project}`}
                        data-testid={`tidy-person-${item.key}`}
                      >
                        <option value="">Nobody (skip)</option>
                        {[...new Set([...personOptions, item.suggested].filter(Boolean))].map((name) => <option key={name} value={name}>{displayPerson(name)}</option>)}
                      </select>
                    </li>
                  ))}
                </ul>
              </Section>
            ) : null}

            {tidy.duplicates.length ? (
              <Section
                testId="tidy-duplicates"
                title={`Drawing IDs used more than once (${fmt(tidy.duplicates.length)}${crossProject ? ` · ${fmt(crossProject)} across projects` : ''})`}
                meta="Nothing changes here — open the drawings and fix the wrong one (e.g. a NOBU HOTEL ID copied to NOBU Residences)."
              >
                <ul className="space-y-1.5">
                  {duplicates.map((item) => (
                    <li key={item.value} className="rounded-lg border border-line bg-surface px-3 py-2 text-[12.5px]">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="min-w-0 truncate font-mono text-[11.5px] font-semibold text-ink-2" title={item.value}>{item.value}</span>
                        {item.crossProject ? <span className="rounded-[5px] bg-status-amber-bg px-1.5 text-[10.5px] font-bold text-status-amber-ink">across projects</span> : null}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {item.drawings.map((drawing) => (
                          <button
                            key={drawing.id}
                            type="button"
                            onClick={() => { onOpenChange(false); onOpenDrawing(byId.get(drawing.id) || drawing); }}
                            className="inline-flex max-w-full items-center gap-1 rounded-md border border-line-2 bg-surface-2 px-1.5 py-0.5 text-[12px] text-ink-2 hover:border-line hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            title={`Open ${drawingTitle(drawing)}`}
                          >
                            <span className="truncate font-semibold">{drawing.project || 'No project'}</span>
                            <span className="font-mono text-[11px] text-ink-3">{drawing.id}</span>
                            <ExternalLink className="h-3 w-3 shrink-0 text-ink-4" aria-hidden="true" />
                          </button>
                        ))}
                      </div>
                    </li>
                  ))}
                </ul>
                {tidy.duplicates.length > duplicates.length ? (
                  <Button type="button" variant="link" size="sm" className="mt-1 h-auto px-0" onClick={() => setShowAllDuplicates(true)}>
                    Show all {fmt(tidy.duplicates.length)}
                  </Button>
                ) : null}
              </Section>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line-2 px-5 py-3">
            <p className="text-[12.5px] text-ink-3" role={error ? 'alert' : undefined} data-testid="tidy-summary">
              {error
                ? <span className="font-semibold text-status-red-ink">{error}</span>
                : changeCount ? <><b className="font-semibold text-ink-2">{fmt(changeCount)}</b> {changeCount === 1 ? 'drawing changes' : 'drawings change'}{chunks.length > 1 ? ` · saved in ${chunks.length} steps of up to 500` : ''}</> : 'Nothing ticked'}
            </p>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
              <Button type="button" onClick={apply} disabled={!changeCount || busy} data-testid="tidy-apply">
                {busy ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
                {busy ? 'Applying…' : changeCount ? `Apply to ${fmt(changeCount)}` : 'Apply'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
